export const SCREEN_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Everyone' },
  { value: 'new_users', label: 'New users' },
  { value: 'existing_users', label: 'Existing users' },
  { value: 'guests', label: 'Guests' },
  { value: 'logged_in', label: 'Logged in' },
];

export const PLATFORM_OPTIONS = [
  { value: '', label: 'All platforms' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

export const SUBJECT_TYPE_OPTIONS = [
  { value: 'installation', label: 'Installation' },
  { value: 'user', label: 'User' },
];

export const SCREEN_COLORS = {
  home: '#1976d2',
  explore: '#2e7d32',
  reel: '#9c27b0',
  arcade: '#ed6c02',
  pulse: '#d32f2f',
};

const FALLBACK_COLORS = ['#0288d1', '#c2185b', '#5d4037', '#00796b', '#455a64', '#6a1b9a'];

export const colorForScreen = (screenKey, index = 0) => {
  if (SCREEN_COLORS[screenKey]) return SCREEN_COLORS[screenKey];
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

export const WEIGHT_RECIPES = [
  { label: '100% Home', weights: { home: 100 } },
  { label: 'Everyone Arcade', weights: { arcade: 100 } },
  { label: 'Everyone Pulse', weights: { pulse: 100 } },
  { label: '30 / 30 / 30 / 10', weights: { reel: 30, arcade: 30, explore: 30, home: 10 } },
  { label: 'Careful Arcade 90 / 10', weights: { home: 90, arcade: 10 } },
  { label: 'Careful Pulse 90 / 10', weights: { home: 90, pulse: 10 } },
];

export const emptyRuleForm = () => ({
  name: '',
  is_enabled: true,
  priority: '100',
  audience: 'all',
  new_user_since: '',
  platform: '',
  min_app_version: '',
  max_app_version: '',
  starts_at: '',
  ends_at: '',
  exclude_test_users: false,
  salt: 'landing-v1',
  fallback_screen_key: 'home',
  notes: '',
  variants: [{ screen_key: 'home', weight: 100, navigation_link: '' }],
});

export const emptyScreenForm = () => ({
  screen_key: '',
  label: '',
  navigation_link: '',
  deep_link: '',
  min_app_version: '',
  is_enabled: true,
  sort_order: '0',
});

export const emptyOverrideForm = () => ({
  subject_type: 'installation',
  subject_key: '',
  screen_key: 'home',
  note: '',
});

export const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

export const fromDateInputValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const formatPercent = (value, digits = 1) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const pct = n > 0 && n <= 1 ? n * 100 : n;
  return `${pct.toFixed(digits)}%`;
};

export const formatNumber = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '0';
  return Number(value).toLocaleString();
};

export const randomSalt = () => {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    bytes.forEach((_, i) => {
      bytes[i] = Math.floor(Math.random() * 256);
    });
  }
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `landing-${hex}`;
};

export const asArray = (value) => {
  if (Array.isArray(value)) return value;
  return [];
};

export const pickList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

export const normalizeScreens = (payload) =>
  pickList(payload, 'screens', 'items', 'rows').map((row) => ({
    screen_key: row.screen_key || row.screenKey,
    label: row.label || row.screen_key || row.screenKey,
    navigation_link: row.navigation_link ?? row.navigationLink ?? '',
    deep_link: row.deep_link ?? row.deepLink ?? '',
    min_app_version: row.min_app_version ?? row.minAppVersion ?? null,
    is_enabled: row.is_enabled !== false && row.isEnabled !== false,
    sort_order: row.sort_order ?? row.sortOrder ?? 0,
    ...row,
  }));

export const normalizeVariants = (rule) =>
  asArray(rule?.variants).map((v) => ({
    screen_key: v.screen_key || v.screenKey,
    weight: Number(v.weight) || 0,
    navigation_link: v.navigation_link ?? v.navigationLink ?? '',
    variant_key: v.variant_key ?? v.variantKey,
  }));

export const normalizeRules = (payload) =>
  pickList(payload, 'rules', 'items', 'rows').map((rule) => ({
    ...rule,
    id: rule.id,
    name: rule.name || 'Untitled',
    is_enabled: rule.is_enabled !== false && rule.isEnabled !== false,
    priority: rule.priority ?? 100,
    audience: rule.audience || 'all',
    platform: rule.platform ?? null,
    salt: rule.salt || 'landing-v1',
    fallback_screen_key: rule.fallback_screen_key || rule.fallbackScreenKey || 'home',
    variants: normalizeVariants(rule),
  }));

export const normalizeOverrides = (payload) =>
  pickList(payload, 'overrides', 'items', 'rows');

export const normalizeAudit = (payload) =>
  pickList(payload, 'items', 'audit', 'entries', 'rows', 'events');

export const screenLabel = (screens, screenKey) => {
  const match = (screens || []).find((s) => s.screen_key === screenKey);
  return match?.label || screenKey || '—';
};

export const audienceLabel = (audience) =>
  AUDIENCE_OPTIONS.find((o) => o.value === audience)?.label || audience || 'Everyone';

export const variantsForEditor = (ruleVariants, screens) => {
  const byKey = new Map((ruleVariants || []).map((v) => [v.screen_key, v]));
  const keys = [];
  (screens || []).forEach((s) => {
    if (s.screen_key && !keys.includes(s.screen_key)) keys.push(s.screen_key);
  });
  (ruleVariants || []).forEach((v) => {
    if (v.screen_key && !keys.includes(v.screen_key)) keys.push(v.screen_key);
  });
  if (keys.length === 0) keys.push('home');
  return keys.map((key) => ({
    screen_key: key,
    weight: Number(byKey.get(key)?.weight) || 0,
    navigation_link: byKey.get(key)?.navigation_link || '',
  }));
};

export const applyRecipe = (recipeWeights, screens, currentVariants = []) => {
  const next = variantsForEditor(currentVariants, screens).map((v) => ({
    ...v,
    weight: Number(recipeWeights[v.screen_key]) || 0,
  }));
  const total = next.reduce((sum, v) => sum + Number(v.weight || 0), 0);
  if (total !== 100) {
    const home = next.find((v) => v.screen_key === 'home') || next[0];
    if (home) home.weight = Math.max(0, Number(home.weight || 0) + (100 - total));
  }
  return next;
};

/** Each weight point owns 100 buckets of 0..9999. */
export const computeBucketRanges = (variants) => {
  let cursor = 0;
  return (variants || [])
    .filter((v) => Number(v.weight) > 0)
    .map((v) => {
      const size = Math.round(Number(v.weight)) * 100;
      const start = cursor;
      const end = size <= 0 ? start - 1 : Math.min(cursor + size - 1, 9999);
      cursor += size;
      return {
        ...v,
        range_start: start,
        range_end: end,
      };
    });
};

export const formatBucketRange = (start, end) => {
  if (start == null || end == null || Number(end) < Number(start)) return '—';
  return `${start}–${end}`;
};

export const variantWeightTotal = (variants) =>
  (variants || []).reduce((sum, v) => sum + (Number(v.weight) || 0), 0);

export const ruleToForm = (rule, screens) => ({
  name: rule?.name || '',
  is_enabled: rule?.is_enabled !== false,
  priority: String(rule?.priority ?? 100),
  audience: rule?.audience || 'all',
  new_user_since: toDateInputValue(rule?.new_user_since ?? rule?.newUserSince),
  platform: rule?.platform || '',
  min_app_version: rule?.min_app_version || rule?.minAppVersion || '',
  max_app_version: rule?.max_app_version || rule?.maxAppVersion || '',
  starts_at: toDateInputValue(rule?.starts_at ?? rule?.startsAt),
  ends_at: toDateInputValue(rule?.ends_at ?? rule?.endsAt),
  exclude_test_users: !!(rule?.exclude_test_users ?? rule?.excludeTestUsers),
  salt: rule?.salt || 'landing-v1',
  fallback_screen_key: rule?.fallback_screen_key || rule?.fallbackScreenKey || 'home',
  notes: rule?.notes || '',
  variants: variantsForEditor(normalizeVariants(rule), screens),
});

export const screenToForm = (row) => ({
  screen_key: row?.screen_key || '',
  label: row?.label || '',
  navigation_link: row?.navigation_link || '',
  deep_link: row?.deep_link || '',
  min_app_version: row?.min_app_version || '',
  is_enabled: row?.is_enabled !== false,
  sort_order: row?.sort_order != null ? String(row.sort_order) : '0',
});

export const validateRuleForm = (form) => {
  const errors = {};
  const name = (form.name || '').trim();
  if (!name) errors.name = 'Name is required';
  else if (name.length > 120) errors.name = 'Name must be 120 characters or fewer';

  const priorityRaw = String(form.priority ?? '').trim();
  if (priorityRaw !== '' && !/^-?\d+$/.test(priorityRaw)) {
    errors.priority = 'Priority must be an integer';
  }

  const total = variantWeightTotal(form.variants);
  if (total !== 100) {
    errors.variants = `Weights must be whole numbers summing to exactly 100 (currently ${total})`;
  } else if (!(form.variants || []).some((v) => Number(v.weight) > 0)) {
    errors.variants = 'At least one screen needs a weight';
  }

  const starts = fromDateInputValue(form.starts_at);
  const ends = fromDateInputValue(form.ends_at);
  if (starts && ends && new Date(starts) > new Date(ends)) {
    errors.ends_at = 'End must be after start';
  }

  return errors;
};

export const validateScreenForm = (form, { isCreate }) => {
  const errors = {};
  if (isCreate) {
    const key = (form.screen_key || '').trim();
    if (!key) errors.screen_key = 'screen_key is required';
    else if (!SCREEN_KEY_PATTERN.test(key)) {
      errors.screen_key = 'Must match [a-z][a-z0-9_]{0,63}';
    }
  }
  if (!(form.label || '').trim()) errors.label = 'Label is required';
  const sortRaw = String(form.sort_order ?? '').trim();
  if (sortRaw !== '' && !/^-?\d+$/.test(sortRaw)) {
    errors.sort_order = 'sort_order must be an integer';
  }
  return errors;
};

export const buildRulePayload = (form) => {
  const variants = (form.variants || [])
    .filter((v) => Number(v.weight) > 0)
    .map((v) => {
      const item = {
        screen_key: v.screen_key,
        weight: Math.round(Number(v.weight)),
      };
      const link = String(v.navigation_link || '').trim();
      if (link) item.navigation_link = link;
      return item;
    });

  return {
    name: form.name.trim(),
    is_enabled: !!form.is_enabled,
    priority: Number.parseInt(String(form.priority || '100'), 10) || 100,
    audience: form.audience || 'all',
    new_user_since: fromDateInputValue(form.new_user_since),
    platform: form.platform || null,
    min_app_version: String(form.min_app_version || '').trim() || null,
    max_app_version: String(form.max_app_version || '').trim() || null,
    starts_at: fromDateInputValue(form.starts_at),
    ends_at: fromDateInputValue(form.ends_at),
    exclude_test_users: !!form.exclude_test_users,
    salt: String(form.salt || '').trim() || 'landing-v1',
    fallback_screen_key: form.fallback_screen_key || 'home',
    notes: String(form.notes || '').trim() || null,
    variants,
  };
};

export const buildScreenPayload = (form, { isCreate }) => {
  const body = {
    label: form.label.trim(),
    navigation_link: String(form.navigation_link || '').trim() || null,
    deep_link: String(form.deep_link || '').trim() || null,
    min_app_version: String(form.min_app_version || '').trim() || null,
    is_enabled: !!form.is_enabled,
    sort_order: Number.parseInt(String(form.sort_order || '0'), 10) || 0,
  };
  if (isCreate) body.screen_key = form.screen_key.trim();
  return body;
};

export const formatVersionWindow = (min, max) => {
  if (!min && !max) return 'any version';
  if (min && !max) return `≥ ${min}`;
  if (!min && max) return `< ${max}`;
  return `${min} ≤ v < ${max}`;
};

export const formatSchedule = (startsAt, endsAt) => {
  if (!startsAt && !endsAt) return 'always on';
  if (startsAt && !endsAt) return `from ${formatDateTime(startsAt)}`;
  if (!startsAt && endsAt) return `until ${formatDateTime(endsAt)}`;
  return `${formatDateTime(startsAt)} → ${formatDateTime(endsAt)}`;
};

export const isSessionExpired = (res) => String(res?.statusCode || '') === '10003';
